// Exchange Rates Service
// Fetches from Worker /api/rates and caches in-memory + sessionStorage.

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const MEMORY_CACHE = {
  data: null,
  fetchedAt: 0,
  ttl: 1000 * 60 * 60 // 1 hour client-side TTL
};

const STORAGE_KEY = 'exchangeRatesCacheV1';

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (Date.now() - parsed.savedAt > MEMORY_CACHE.ttl) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function saveToStorage(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {}
}

export async function getExchangeRates({ codes = ['USD','RUB','EUR','CNY','KZT','UZS'], forceRefresh = false } = {}) {
  // Serve from memory cache
  if (!forceRefresh && MEMORY_CACHE.data && Date.now() - MEMORY_CACHE.fetchedAt < MEMORY_CACHE.ttl) {
    return MEMORY_CACHE.data;
  }

  // Try sessionStorage
  if (!forceRefresh && !MEMORY_CACHE.data) {
    const stored = loadFromStorage();
    if (stored) {
      MEMORY_CACHE.data = stored;
      MEMORY_CACHE.fetchedAt = Date.now();
      return stored;
    }
  }

  const query = `codes=${encodeURIComponent(codes.join(','))}`;
  const url = `${API_BASE_URL}/api/rates?${query}`.replace(/\/$/, '');
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!res.ok) throw new Error(`Failed to fetch rates (${res.status})`);
  const json = await res.json();
  if (!json.rates) throw new Error('Malformed rates response');

  MEMORY_CACHE.data = json;
  MEMORY_CACHE.fetchedAt = Date.now();
  saveToStorage(json);
  return json;
}

export function convert(amount, from, to, ratesPayload) {
  if (!ratesPayload || !ratesPayload.rates) return amount;
  if (from === to) return amount;
  // All rates are KGS per 1 unit of foreign currency (base = KGS)
  const table = ratesPayload.rates;
  const toUpper = c => (c || 'KGS').toUpperCase();
  const f = toUpper(from);
  const t = toUpper(to);

  // If from is not KGS, convert to KGS first: amount * rate(from)
  let amountInKgs = amount;
  if (f !== 'KGS') {
    if (!table[f]) return amount; // unknown code fallback
    amountInKgs = amount * table[f];
  }

  if (t === 'KGS') return amountInKgs;
  if (!table[t]) return amount; // unknown target fallback
  return amountInKgs / table[t];
}

import { formatAmount } from '../utils/currency.js';

export function format(amount, code, opts = {}) {
  return formatAmount(amount, code);
}
